import { Dispatch, FC, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';

import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { changeNewProductField, clearItemInNewProduct, hideSecondaryPanel } from 'store/productManagement/reducer';
import { useOnChangePanelVisibility } from 'components/pages/PCM/hooks/useOnChangePanelVisibility';
import locationSvg from 'assets/icons/location.svg';
import SectionButton, { SectionButtonTheme } from 'lib/SectionButton';

import { Store } from 'components/common/types/Store.types';
import { ProductFieldName, SelectionPermission } from 'store/productManagement/reducer.types';
import { DirectSalesCodeNameModel } from 'components/common/types/DirectSalesCampaign.types';
import { sortByCategoryIncludeExclude } from 'components/pages/Planner/PlannerSections/FiltersContent/utils';
import { getIsReadOnly } from 'store/productManagement/selectors';
import ProductLocationsPanel from './ProductLocationsPanel';
import { isValueRequired } from '../../utils/isValueRequired';
import { getNoRestrictionsDisplayValue } from '../../utils/getNoRestrictionsDisplayValue';
import { PERMISSION_LABELS } from '../../consts';

const getTheme = (selectedPermission?: SelectionPermission): SectionButtonTheme => {
  return selectedPermission ? SectionButtonTheme.SOLID : SectionButtonTheme.DASHED;
};

const getCategorizedValues = (
  selectedLocations?: DirectSalesCodeNameModel[] | undefined,
):
  | Record<
      string,
      { included?: DirectSalesCodeNameModel[] | undefined; excluded?: DirectSalesCodeNameModel[] | undefined }
    >
  | [] => {
  return selectedLocations ? sortByCategoryIncludeExclude(selectedLocations) : [];
};

const handleLocationChange = ({
  selectedLocations,
  locationsToRemove,
  included,
  selectedPermission,
  dispatch,
  fieldName,
}: {
  selectedLocations: DirectSalesCodeNameModel[];
  locationsToRemove: string[];
  included?: boolean;
  selectedPermission: SelectionPermission;
  dispatch: Dispatch<AnyAction>;
  fieldName: ProductFieldName;
}): void => {
  const locationsSet = new Set(locationsToRemove);

  const newSelectedLocations = selectedLocations.filter(
    (value) => !locationsSet.has(value.name) || (included !== undefined && value.include !== included),
  );

  if (newSelectedLocations.length) {
    dispatch(
      changeNewProductField({
        fieldName,
        value: newSelectedLocations,
        selectedPermission: { code: selectedPermission, name: PERMISSION_LABELS[selectedPermission] },
      }),
    );
  } else {
    dispatch(clearItemInNewProduct({ fieldName }));
  }
};

const handleLocationChangeByInclude = ({
  selectedLocations,
  included,
  dispatch,
  fieldName,
  selectedPermission,
}: {
  selectedLocations: DirectSalesCodeNameModel[];
  included?: boolean;
  dispatch: Dispatch<AnyAction>;
  fieldName: ProductFieldName;
  selectedPermission: SelectionPermission;
}): void => {
  const newSelectedLocations = selectedLocations.filter((value) => value.include !== included);
  if (!newSelectedLocations.length) {
    dispatch(clearItemInNewProduct({ fieldName }));
    return;
  }

  dispatch(
    changeNewProductField({
      fieldName,
      value: newSelectedLocations,
      selectedPermission: { code: selectedPermission, name: PERMISSION_LABELS[selectedPermission] },
    }),
  );
};

const ProductLocation: FC = () => {
  const dispatch = useDispatch();
  const isReadOnly = useSelector(getIsReadOnly);
  const mediaType = useSelector((state: Store) => state.productManagement.newProduct.mediaType?.value);
  const channels = useSelector((state: Store) => state.productManagement.newProduct.channels?.value);
  const secondaryPanelType = useSelector((state: Store) => state.productManagement.secondaryPanel);
  const selectedLocations = useSelector((state: Store) => state.productManagement.newProduct.locations?.value);
  const selectedPermission = useSelector(
    (state: Store) => state.productManagement.newProduct.locations?.selectionPermission,
  );

  const onChangePanelVisibility = useOnChangePanelVisibility();
  const fieldName = ProductFieldName.LOCATIONS;
  const isLocationPanelOpen = secondaryPanelType === PlannerSecondaryPanelType.LOCATION;

  const categorizedValues = useMemo(() => getCategorizedValues(selectedLocations), [selectedLocations]);

  const onRemoveLocationChip = (locationsToRemove?: string[], included?: boolean): void => {
    if (!selectedPermission) return;

    if (!locationsToRemove) {
      if (included !== undefined) {
        handleLocationChangeByInclude({
          selectedLocations: selectedLocations || [],
          included,
          dispatch,
          fieldName,
          selectedPermission,
        });
        return;
      }
      dispatch(clearItemInNewProduct({ fieldName }));
      return;
    }

    if (!locationsToRemove.length) return;

    handleLocationChange({
      selectedLocations: selectedLocations || [],
      locationsToRemove,
      included,
      selectedPermission,
      dispatch,
      fieldName,
    });
  };

  return (
    <>
      <SectionButton
        dataTestId="product-button-locations"
        label="Location"
        icon={locationSvg}
        isDisabled={!mediaType || !channels?.length}
        isEllipsisDisabled={isReadOnly}
        theme={getTheme(selectedPermission)}
        isActive={isLocationPanelOpen}
        selected={
          isValueRequired(selectedPermission) ? categorizedValues : getNoRestrictionsDisplayValue(selectedPermission)
        }
        onClick={() => onChangePanelVisibility(PlannerSecondaryPanelType.LOCATION)}
        onClear={() => {
          dispatch(clearItemInNewProduct({ fieldName }));
          dispatch(hideSecondaryPanel());
        }}
        onRemoveChip={onRemoveLocationChip}
      />
      {isLocationPanelOpen && (
        <ProductLocationsPanel onCancel={() => dispatch(hideSecondaryPanel())} isReadOnly={isReadOnly} />
      )}
    </>
  );
};

export default ProductLocation;
